import { useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { VoucherDiscountType, VoucherFilter, VoucherStatus } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormLayout';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { formatCents, formatDateTime } from '@/lib/format';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { useEmailGuard } from '@/lib/useEmailCheck';
import {
  voucherDiscountLabel,
  voucherStatusLabel,
  voucherStatusTone,
} from '@/lib/labels';
import { Can } from '@/lib/permissions';
import {
  optionsOf,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
} from '@/hooks/useUrlState';
import {
  VOUCHER_COLUMN_SORT,
  VOUCHER_STATUS_LABELS,
  VOUCHERS_DEFAULT_SORT,
  vouchersUrlSchema,
} from './vouchersUrlState';

const VouchersDoc = graphql(/* GraphQL */ `
  query Vouchers(
    $filter: VoucherFilter
    $page: Int
    $pageSize: Int
    $sort: VoucherSort
  ) {
    vouchers(filter: $filter, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        status
        userEmail
        discountType
        discountPercentage
        discountAmountCents
        productName
        label
        ruleName
        issuedAt
        expiresAt
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const VoucherRulesForIssueDoc = graphql(/* GraphQL */ `
  query VoucherRulesForIssue {
    voucherRules(includeInactive: false) {
      id
      name
    }
  }
`);

const ProductsForVoucherRuleDoc = graphql(/* GraphQL */ `
  query ProductsForVoucherRule {
    products(includeInactive: false, page: 1, pageSize: 200) {
      nodes {
        id
        name
      }
    }
  }
`);

const IssueVoucherDoc = graphql(/* GraphQL */ `
  mutation IssueVoucher($input: IssueVoucherInput!) {
    issueVoucher(input: $input) {
      id
      status
    }
  }
`);

const RevokeVoucherDoc = graphql(/* GraphQL */ `
  mutation RevokeVoucher($id: UUID!, $note: String) {
    revokeVoucher(id: $id, note: $note) {
      id
      status
    }
  }
`);

const ExportVouchersCsvDoc = graphql(/* GraphQL */ `
  query ExportVouchersCsv($filter: VoucherFilter) {
    exportVouchersCsv(filter: $filter) {
      url
      expiresAt
    }
  }
`);

interface VoucherRow {
  id: string;
  status: VoucherStatus;
  userEmail?: string | null;
  discountType: string;
  discountPercentage?: number | null;
  discountAmountCents?: unknown;
  productName?: string | null;
  label?: string | null;
  issuedAt: string;
  expiresAt?: string | null;
  ruleName?: string | null;
}

export function VouchersListPage() {
  const { state: url, patch, setFilters } = useUrlState(vouchersUrlSchema);
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, VOUCHERS_DEFAULT_SORT));
  };
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueEmail, setIssueEmail] = useState('');
  const [issueRuleId, setIssueRuleId] = useState('');
  const [issueDiscountType, setIssueDiscountType] =
    useState<VoucherDiscountType>('PERCENTAGE');
  const [issuePercent, setIssuePercent] = useState('');
  const [issueAmount, setIssueAmount] = useState('');
  const [issueProductId, setIssueProductId] = useState('');
  const [issueLabel, setIssueLabel] = useState('');
  const [issueValidityDays, setIssueValidityDays] = useState('30');
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issuedId, setIssuedId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Record<string, boolean>>({});

  const filter = useMemo<VoucherFilter>(
    () => ({
      search: url.search || null,
      status: url.status ? [url.status] : null,
    }),
    [url.search, url.status],
  );

  const sort = useMemo(
    () => ({ field: VOUCHER_COLUMN_SORT[url.sort], direction: (url.dir === 'asc' ? 'ASC' : 'DESC') as 'ASC' | 'DESC' }),
    [url.sort, url.dir],
  );

  const { data, previousData, loading, error, refetch } = useQuery(VouchersDoc, {
    variables: { filter, page: url.page, pageSize: url.size, sort },
    errorPolicy: 'all',
  });
  const viewData = data ?? previousData;
  const rulesQuery = useQuery(VoucherRulesForIssueDoc, { skip: !issueOpen, errorPolicy: 'all' });
  const productsQuery = useQuery(ProductsForVoucherRuleDoc, {
    skip: !issueOpen || !!issueRuleId,
    errorPolicy: 'all',
  });
  const [issueVoucher, { loading: issuing }] = useMutation(IssueVoucherDoc);
  const [checking, setChecking] = useState(false);
  const emailGuard = useEmailGuard();
  const [revokeVoucher] = useMutation(RevokeVoucherDoc);
  const [exportCsv] = useLazyQuery(ExportVouchersCsvDoc);

  const rows: VoucherRow[] = useMemo(
    () =>
      (viewData?.vouchers.nodes ?? []).map((n) => ({
        id: n.id,
        status: n.status,
        userEmail: n.userEmail,
        discountType: n.discountType,
        discountPercentage: n.discountPercentage,
        discountAmountCents: n.discountAmountCents,
        productName: n.productName,
        label: n.label,
        issuedAt: n.issuedAt,
        expiresAt: n.expiresAt,
        ruleName: n.ruleName,
      })),
    [viewData],
  );
  const pageInfo = viewData?.vouchers.pageInfo;

  const columns = useMemo<ColumnDef<VoucherRow>[]>(
    () => [
      {
        id: 'label',
        header: 'Voucher',
        enableSorting: false,
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {row.original.label || voucherDiscountLabel(row.original.discountType as never)}
            </p>
            <p className="text-xs text-gray-500">
              {row.original.discountPercentage != null
                ? `${row.original.discountPercentage}%`
                : null}
              {row.original.discountAmountCents != null
                ? formatCents(row.original.discountAmountCents as number)
                : null}
              {row.original.productName ?? ''}
              {row.original.ruleName ? ` · ${row.original.ruleName}` : ' · issued by admin'}
            </p>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={voucherStatusTone(row.original.status)}>
            {voucherStatusLabel(row.original.status)}
          </Badge>
        ),
      },
      {
        id: 'userEmail',
        header: 'Receiver',
        // Go ignores `sort` on this grid today, so do not offer a sort control
        // that silently does nothing (the schema has USER_EMAIL ready for it).
        enableSorting: false,
        cell: ({ row }) => row.original.userEmail || '—',
      },
      {
        id: 'issuedAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.issuedAt),
      },
      {
        id: 'expiresAt',
        header: 'Expires',
        cell: ({ row }) => formatDateTime(row.original.expiresAt),
      },
    ],
    [],
  );

  const issue = async (address: string) => {
    setIssueError(null);
    if (!address) {
      setIssueError('Email is required.');
      return;
    }
    if (!issueRuleId) {
      if (issueDiscountType === 'PERCENTAGE') {
        const n = Number.parseFloat(issuePercent);
        if (!Number.isFinite(n) || n <= 0) {
          setIssueError('Percentage benefit needs a whole percent.');
          return;
        }
      }
      if (issueDiscountType === 'AMOUNT') {
        const n = Number.parseFloat(issueAmount);
        if (!Number.isFinite(n) || n <= 0) {
          setIssueError('Fixed-amount benefit needs a euro amount.');
          return;
        }
      }
      if (issueDiscountType === 'FREE_PRODUCT' && !issueProductId) {
        setIssueError('Free-product benefit needs a catalog product.');
        return;
      }
    }
    setChecking(true);
    try {
      if (!(await emailGuard.screen(address))) return;
      const result = await issueVoucher({
        variables: {
          input: {
            email: address,
            voucherRuleId: issueRuleId || null,
            label: issueLabel.trim() || null,
            validityDays: Number.parseInt(issueValidityDays, 10) || 30,
            ...(issueRuleId
              ? {}
              : {
                  discountType: issueDiscountType,
                  discountPercentage:
                    issueDiscountType === 'PERCENTAGE'
                      ? Number.parseFloat(issuePercent)
                      : null,
                  discountAmount:
                    issueDiscountType === 'AMOUNT'
                      ? Number.parseFloat(issueAmount)
                      : null,
                  productId:
                    issueDiscountType === 'FREE_PRODUCT'
                      ? issueProductId
                      : null,
                }),
          },
        },
      });
      const minted = result.data?.issueVoucher.id;
      setIssuedId(minted ?? null);
      setIssueOpen(false);
      setIssueEmail('');
      setIssueRuleId('');
      setIssueDiscountType('PERCENTAGE');
      setIssuePercent('');
      setIssueAmount('');
      setIssueProductId('');
      setIssueLabel('');
      setIssueValidityDays('30');
      await refetch();
    } catch (err) {
      emailGuard.refused(err, address);
      setIssueError(graphqlErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Vouchers
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Minted rewards for this venue. They are not bought and carry no point
            price. Revoke requires a passkey session.
          </p>
        </div>
        <Can permission="voucher:issue">
          <Button onClick={() => setIssueOpen(true)}>Issue by email</Button>
        </Can>
      </div>

      {error ? <QueryErrorNotice error={error} title="Could not load vouchers" /> : null}
      {issuedId ? (
        <p className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-950/40 dark:text-brand-200">
          Issued voucher{' '}
          <span id="admin.vouchers.issuedId" className="font-mono">
            {issuedId}
          </span>
        </p>
      ) : null}
      {revokeError ? (
        <p className="text-sm text-red-600 dark:text-red-400">{revokeError}</p>
      ) : null}

      <FilterBar
        search={{
          id: 'admin.vouchers.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search…',
        }}
        primary={
          <Select
            id="admin.vouchers.status"
            value={url.status}
            onChange={(e) => setFilters({ status: e.target.value as VoucherStatus | '' })}
            options={optionsOf(VOUCHER_STATUS_LABELS, 'Any status')}
          />
        }
      />

      <DataGrid<VoucherRow>
        data={rows}
        columns={columns}
        loading={loading && !viewData}
        rowId={(row) => row.id}
        selection={{ state: selection, onChange: setSelection }}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
        onExport={async () => {
          const result = await exportCsv({ variables: { filter } });
          const url = result.data?.exportVouchersCsv.url;
          if (url) await downloadAuthenticatedCsv(url);
        }}
        actions={[
          {
            id: 'revoke',
            label: 'Revoke',
            variant: 'danger',
            isAllowed: (selected) =>
              selected.every((r) => r.status === 'ISSUED' || r.status === 'RESERVED'),
            onRun: async (selected) => {
              setRevokeError(null);
              const note = window.prompt('Revoke note (optional)') ?? undefined;
              try {
                for (const row of selected) {
                  await revokeVoucher({ variables: { id: row.id, note } });
                }
                await refetch();
              } catch (err) {
                setRevokeError(graphqlErrorMessage(err));
              }
            },
          },
        ]}
      />

      <Modal
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        size="lg"
        title="Issue a voucher by email"
        description="Works for visitors with or without an account. Customer.io sends the QR (send_to_unsubscribed). Copying a rule does not consume that rule's once-per-user slot."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setIssueOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              loading={issuing || checking}
              onClick={() => void issue(issueEmail.trim())}
            >
              Issue
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Email"
            required
            description="An unknown address gets a provisional account that cannot log in and is not opted in to marketing by this. Signing up with the address later claims the account with its vouchers."
          >
            <Input
              id="admin.vouchers.issueEmail"
              type="email"
              value={issueEmail}
              onChange={(e) => {
                setIssueEmail(e.target.value);
                emailGuard.changed();
              }}
              required
            />
          </FormField>
          <GuardSuggestion
            guard={emailGuard}
            idPrefix="admin.vouchers.issueEmail"
            disabled={issuing || checking}
            onAnswer={(address) => {
              setIssueEmail(address);
              void issue(address);
            }}
          />
          <FormField
            label="Copy benefit from rule (optional)"
            description="When omitted, set a one-off benefit below."
          >
            <Select
              id="admin.vouchers.issueRule"
              value={issueRuleId}
              onChange={(e) => setIssueRuleId(e.target.value)}
              options={[
                { value: '', label: 'No rule (one-off)' },
                ...(rulesQuery.data?.voucherRules ?? []).map((r) => ({
                  value: r.id,
                  label: r.name,
                })),
              ]}
            />
          </FormField>
          {!issueRuleId ? (
            <>
              <FormField label="Benefit type" required>
                <Select
                  id="admin.vouchers.issueDiscountType"
                  value={issueDiscountType}
                  onChange={(e) =>
                    setIssueDiscountType(e.target.value as VoucherDiscountType)
                  }
                  options={[
                    { value: 'PERCENTAGE', label: 'Percentage off' },
                    { value: 'AMOUNT', label: 'Fixed euro amount' },
                    { value: 'FREE_PRODUCT', label: 'Free product' },
                  ]}
                />
              </FormField>
              {issueDiscountType === 'PERCENTAGE' ? (
                <FormField label="Percent" required>
                  <Input
                    id="admin.vouchers.issuePercent"
                    type="number"
                    min={1}
                    max={100}
                    value={issuePercent}
                    onChange={(e) => setIssuePercent(e.target.value)}
                    required
                  />
                </FormField>
              ) : null}
              {issueDiscountType === 'AMOUNT' ? (
                <FormField label="Amount (€)" required>
                  <Input
                    id="admin.vouchers.issueAmount"
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={issueAmount}
                    onChange={(e) => setIssueAmount(e.target.value)}
                    required
                  />
                </FormField>
              ) : null}
              {issueDiscountType === 'FREE_PRODUCT' ? (
                <FormField label="Product" required>
                  <Select
                    id="admin.vouchers.issueProduct"
                    value={issueProductId}
                    onChange={(e) => setIssueProductId(e.target.value)}
                    options={[
                      { value: '', label: 'Select a product…' },
                      ...(productsQuery.data?.products.nodes ?? []).map((p) => ({
                        value: p.id,
                        label: p.name,
                      })),
                    ]}
                  />
                </FormField>
              ) : null}
              <FormField
                label="Label"
                description="Shown to the guest in the app and the voucher mail. Leave empty to use the rule's label."
              >
                <Input
                  id="admin.vouchers.issueLabel"
                  value={issueLabel}
                  maxLength={80}
                  onChange={(e) => setIssueLabel(e.target.value)}
                />
              </FormField>
              <FormField label="Validity (days)" required>
                <Input
                  id="admin.vouchers.issueValidityDays"
                  type="number"
                  min={1}
                  value={issueValidityDays}
                  onChange={(e) => setIssueValidityDays(e.target.value)}
                  required
                />
              </FormField>
            </>
          ) : null}
          {issueError ? <p className="text-sm text-red-600">{issueError}</p> : null}
        </div>
      </Modal>
    </div>
  );
}
